import { useEffect, useRef, useState } from "react";

import axios from "axios";

import {
  loadStripeTerminal
} from "@stripe/terminal-js";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";

import { Button } from "@/components/ui/button";

import {
  Loader2,
  CreditCard,
  Wifi,
  CheckCircle2
} from "lucide-react";

import { toast } from "sonner";

// ---------------------------------------------------
// COMPONENT
// ---------------------------------------------------

export default function POSTerminalPaymentModal({

  open,
  onClose,

  orderId,
  amount,

  getAuthHeader,

  onPaymentSuccess

}) {

  const API = `${window.location.origin}/api`;

  const terminalRef = useRef(null);

  const [loading, setLoading] = useState(false);

  const [readerConnected, setReaderConnected] =
    useState(false);

  const [readerLabel, setReaderLabel] =
    useState("");

  const [status, setStatus] =
    useState("Waiting to connect terminal");

  // ---------------------------------------------------
  // FETCH CONNECTION TOKEN
  // ---------------------------------------------------

  const fetchConnectionToken = async () => {

    const response = await axios.post(
      `${API}/pos/connection-token`,
      {},
      {
        headers: getAuthHeader()
      }
    );

    return response.data.secret;
  };

  // ---------------------------------------------------
  // INITIALIZE TERMINAL
  // ---------------------------------------------------

  const initializeTerminal = async () => {

    try {

      setLoading(true);

      setStatus(
        "Initializing Stripe Terminal..."
      );

      const StripeTerminal =
        await loadStripeTerminal();

      const terminal =
        StripeTerminal.create({

          onFetchConnectionToken:
            fetchConnectionToken,

          onUnexpectedReaderDisconnect:
            () => {

              toast.error(
                "Reader disconnected"
              );

              setReaderConnected(false);

              setReaderLabel("");

              setStatus(
                "Reader disconnected"
              );
            }
        });

      terminalRef.current = terminal;

      // ---------------------------------------------------
      // Discover Readers
      // ---------------------------------------------------

      setStatus(
        "Searching for readers..."
      );

      const discoverResult =
        await terminal.discoverReaders({
          simulated: true
        });

      if (
        discoverResult.error
      ) {

        console.error(
          discoverResult.error
        );

        toast.error(
          discoverResult.error.message
        );

        return;
      }

      if (
        !discoverResult.discoveredReaders
          .length
      ) {

        toast.error(
          "No Stripe readers found"
        );

        return;
      }

      // ---------------------------------------------------
      // Connect Reader
      // ---------------------------------------------------

      const selectedReader =
        discoverResult.discoveredReaders[0];

      const connectResult =
        await terminal.connectReader(
          selectedReader
        );

      if (connectResult.error) {

        toast.error(
          connectResult.error.message
        );

        return;
      }

      setReaderConnected(true);

      setReaderLabel(
        selectedReader.label ||
        "Stripe Reader"
      );

      setStatus(
        "Reader connected successfully"
      );

      toast.success(
        "Stripe terminal connected"
      );

    } catch (error) {

      console.error(error);

      toast.error(
        "Failed to initialize terminal"
      );

    } finally {

      setLoading(false);
    }
  };

  // ---------------------------------------------------
  // CREATE PAYMENT INTENT
  // ---------------------------------------------------

  const createPaymentIntent =
    async () => {

      const response = await axios.post(
        `${API}/pos/payments/create-intent`,
        {
          order_id: orderId
        },
        {
          headers: getAuthHeader()
        }
      );

      return response.data;
    };

  // ---------------------------------------------------
  // HANDLE CARD PAYMENT
  // ---------------------------------------------------

  const handleCardPayment =
    async () => {

      try {

        if (!terminalRef.current) {

          toast.error(
            "Terminal not initialized"
          );

          return;
        }

        setLoading(true);

        // ---------------------------------------------------
        // Create Intent
        // ---------------------------------------------------

        setStatus(
          "Creating payment..."
        );

        const paymentData =
          await createPaymentIntent();

        // ---------------------------------------------------
        // Retrieve Payment Intent
        // ---------------------------------------------------

       // ---------------------------------------------------
// Collect Payment Method
// ---------------------------------------------------

setStatus(
  "Waiting for customer to tap/swipe card..."
);

const terminal =
  terminalRef.current;

const collectResult =
  await terminal.collectPaymentMethod(
    paymentData.client_secret
  );

if (
  collectResult.error
) {

  toast.error(
    collectResult.error.message
  );

  return;
}
        // ---------------------------------------------------
        // Process Payment
        // ---------------------------------------------------

        setStatus(
          "Processing payment..."
        );

        const processResult =
          await terminal.processPayment(
            collectResult.paymentIntent
          );

        if (
          processResult.error
        ) {

          toast.error(
            processResult.error.message
          );

          return;
        }

        // ---------------------------------------------------
        // VERIFY PAYMENT
        // ---------------------------------------------------

        await axios.post(
          `${API}/pos/payments/payment-success`,
          {
            payment_intent_id:
              processResult.paymentIntent.id
          },
          {
            headers: getAuthHeader()
          }
        );

        // ---------------------------------------------------
        // MARK ORDER PAID
        // ---------------------------------------------------

        await axios.patch(
          `${API}/orders/${orderId}/payment`,
          {},
          {
            params: {
              payment_method: "card"
            },
            headers: getAuthHeader()
          }
        );

        // ---------------------------------------------------
        // SUCCESS
        // ---------------------------------------------------

        setStatus(
          "Payment successful"
        );

        toast.success(
          "Payment completed"
        );

        if (onPaymentSuccess) {
          await onPaymentSuccess();
        }

        onClose();

      } catch (error) {

        console.error(error);

        toast.error(
          error?.response?.data?.detail ||
          "Payment failed"
        );

      } finally {

        setLoading(false);
      }
    };

  // ---------------------------------------------------
  // INIT ON OPEN
  // ---------------------------------------------------

  useEffect(() => {

    if (open) {

      initializeTerminal();
    }

  }, [open]);

  // ---------------------------------------------------
  // UI
  // ---------------------------------------------------

  return (

    <Dialog
      open={open}
      onOpenChange={onClose}
    >

      <DialogContent className="max-w-lg">

        <DialogHeader>

          <DialogTitle>
            Stripe Terminal Payment
          </DialogTitle>

        </DialogHeader>

        <div className="space-y-5 py-4">

          {/* Amount */}

          <div className="rounded-2xl bg-blue-50 border border-blue-200 p-5">

            <p className="text-sm text-gray-600">
              Amount
            </p>

            <h2 className="mt-2 text-4xl font-bold text-blue-700">
              ${Number(amount).toFixed(2)}
            </h2>

          </div>

          {/* Reader Status */}

          <div className="rounded-2xl border p-4">

            <div className="flex items-center gap-3">

              {readerConnected ? (

                <CheckCircle2 className="text-green-600" />

              ) : (

                <Wifi className="text-orange-500" />

              )}

              <div>

                <p className="font-semibold">
                  {readerConnected
                    ? "Reader Connected"
                    : "Reader Not Connected"}
                </p>

                <p className="text-sm text-gray-500">
                  {readerLabel || status}
                </p>

              </div>

            </div>

          </div>

          {/* Instructions */}

          <div className="rounded-xl bg-gray-50 p-4 text-sm text-gray-700">

            <p className="font-semibold mb-2">
              Customer Payment Methods
            </p>

            <ul className="space-y-1">
              <li>• Tap Card</li>
              <li>• Insert Chip Card</li>
              <li>• Swipe Card</li>
              <li>• Apple Pay</li>
              <li>• Google Pay</li>
            </ul>

          </div>

          {/* Pay Button */}

          <Button
            disabled={
              loading ||
              !readerConnected
            }
            onClick={handleCardPayment}
            className="h-14 w-full text-lg"
          >

            {loading ? (

              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                {status}
              </>

            ) : (

              <>
                <CreditCard className="mr-2 h-5 w-5" />
                Charge Card
              </>

            )}

          </Button>

        </div>

      </DialogContent>

    </Dialog>
  );
}